import { useState } from 'react'
import { Check, Loader2, Luggage, Plus, Trash2, Users } from 'lucide-react'

import { Field } from '../../components/Field'
import { ROLLEN, type Role } from '../../data/api'
import { useTrip } from '../../data/tripContext'
import {
  fehlerSatz,
  istEmail,
  normalisiereEmail,
  rolleErklaerung,
  rolleName,
} from '../../data/trips'

/**
 * Der Reiter "Reise": worin man gerade schreibt, mit wem, und der Weg zu einer
 * anderen.
 *
 * Alles, was die Reise als Objekt betrifft, steht hier zusammen - Name,
 * Mitglieder, Wechsel, Löschen. Die Rahmendaten der Fahrt (Fahrzeug,
 * Verbrauch, Abfahrt) bleiben im Reiter "Daten": das sind Inhalte der Reise und
 * keine Aussagen darüber, wem sie gehört.
 */
export function TripView() {
  const { aktiv, rolle, darfVerwalten, istAltbestand, trips, oeffne, zurListe } = useTrip()

  return (
    <section className="seite daten">
      <div className="block">
        <h4>
          <Luggage size={14} /> {aktiv?.name ?? 'Keine Reise'}
        </h4>
        <p>
          Deine Rolle: <b>{rolleName(rolle)}</b>. {rolleErklaerung(rolle)}
          {aktiv?.createdBy && ` Angelegt von ${aktiv.createdBy}.`}
        </p>

        {istAltbestand && (
          <p className="hinweis">
            Dieser Bestand ist noch keiner Reise auf dem Server zugeordnet und wird deshalb nicht
            abgeglichen. Sobald wieder Netz da ist, ordnet die App ihn der Reise zu, aus der er
            stammt. Bis dahin bleibt alles auf diesem Gerät.
          </p>
        )}

        {darfVerwalten && !istAltbestand && <NameAendern />}

        <div className="knopfreihe">
          <button type="button" className="knopf still" onClick={() => zurListe()}>
            Reise wechseln
          </button>
          {trips
            .filter((t) => t.id !== aktiv?.id)
            .slice(0, 4)
            .map((t) => (
              <button key={t.id} type="button" className="knopf still" onClick={() => oeffne(t.id)}>
                {t.name}
              </button>
            ))}
        </div>
        <p className="abschluss">
          Ein Wechsel lässt beide Bestände liegen, wo sie sind: jede Reise hat ihren eigenen
          Speicher auf diesem Gerät, samt dem, was noch auf den Abgleich wartet.
        </p>
      </div>

      {!istAltbestand && <Mitglieder />}
      {darfVerwalten && !istAltbestand && <ReiseLoeschen />}
    </section>
  )
}

/** Umbenennen. Nur die Verwaltung darf das, der Server weist alles andere ab. */
function NameAendern() {
  const { aktiv, umbenennen } = useTrip()
  const [name, setName] = useState(aktiv?.name ?? '')
  const [laeuft, setLaeuft] = useState(false)
  const [meldung, setMeldung] = useState<string | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)

  async function speichern() {
    const sauber = name.trim()
    if (!sauber || sauber === aktiv?.name) return
    setLaeuft(true)
    setFehler(null)
    setMeldung(null)
    try {
      await umbenennen(sauber)
      setMeldung('Name geändert.')
    } catch (err) {
      console.error('Umbenennen fehlgeschlagen', err)
      setFehler(fehlerSatz(err))
    } finally {
      setLaeuft(false)
    }
  }

  return (
    <>
      <div className="raster-2">
        <Field label="Name der Reise">
          <input
            value={name}
            autoComplete="off"
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void speichern()
            }}
          />
        </Field>
      </div>
      <div className="knopfreihe">
        <button
          type="button"
          className="knopf"
          disabled={laeuft || name.trim() === '' || name.trim() === aktiv?.name}
          onClick={() => void speichern()}
        >
          {laeuft ? <Loader2 size={14} className="dreht" /> : <Check size={14} />} Namen speichern
        </button>
      </div>
      {meldung && <p className="hinweis">{meldung}</p>}
      {fehler && <p className="hinweis">{fehler}</p>}
    </>
  )
}

/**
 * Die Mitgliederliste.
 *
 * Sehen darf sie, wer in der Reise schreiben darf - der Server gibt sie ab der
 * Rolle Bearbeiten heraus. Wer nur zusieht, sieht auch die Liste nicht; sie
 * anzufragen brachte nur ein 403 und nie eine Antwort.
 *
 * Ändern darf sie nur die Verwaltung, und die eigene Zeile niemand: das ist
 * die Regel, die den Zustand "Reise ohne Verwaltung" unerreichbar macht, und
 * der Server setzt sie durch. Die Oberfläche zeigt hier deshalb gar keine
 * Knöpfe an, statt sie ins Leere laufen zu lassen.
 */
function Mitglieder() {
  const { mitglieder, meineEmail, darfSchreiben, darfVerwalten, setzeMitglied, entferneMitglied } =
    useTrip()
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuftFuer, setLaeuftFuer] = useState<string | null>(null)

  const meine = normalisiereEmail(meineEmail)

  async function fuehreAus(email: string, arbeit: () => Promise<void>) {
    setLaeuftFuer(email)
    setFehler(null)
    try {
      await arbeit()
    } catch (err) {
      console.error('Mitglied ändern fehlgeschlagen', err)
      setFehler(fehlerSatz(err))
    } finally {
      setLaeuftFuer(null)
    }
  }

  return (
    <div className="block">
      <h4>
        <Users size={14} /> Mitglieder
        {mitglieder && <span className="fortschritt">{mitglieder.length}</span>}
      </h4>

      {!darfSchreiben && (
        <p className="leer">
          Mit der Rolle Nur lesen bleibt die Mitgliederliste verschlossen — wer sonst noch mitfährt,
          sagt dir, wer dich eingeladen hat.
        </p>
      )}
      {darfSchreiben && mitglieder === null && (
        <p className="leer">Mitglieder sind noch nicht geladen.</p>
      )}
      {mitglieder?.length === 0 && <p className="leer">Niemand eingetragen.</p>}

      {mitglieder && mitglieder.length > 0 && (
        <ul className="eintragsliste">
          {mitglieder.map((mitglied) => {
            const selbst = normalisiereEmail(mitglied.email) === meine && meine !== ''
            const beschaeftigt = laeuftFuer === mitglied.email
            return (
              <li key={mitglied.email} className="eintrag mitgliedszeile">
                <span>
                  <b>{mitglied.email}</b>
                  {selbst && <span className="marker">Du</span>}
                </span>

                {darfVerwalten && !selbst ? (
                  <div className="eintrag-knoepfe">
                    <Field label="Rolle">
                      <select
                        value={mitglied.role}
                        disabled={beschaeftigt}
                        onChange={(e) => {
                          const rolle = e.target.value as Role
                          void fuehreAus(mitglied.email, () => setzeMitglied(mitglied.email, rolle))
                        }}
                      >
                        {ROLLEN.map((r) => (
                          <option key={r} value={r}>
                            {rolleName(r)}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <button
                      type="button"
                      className="knopf gefahr"
                      disabled={beschaeftigt}
                      onClick={() => {
                        if (!window.confirm(`${mitglied.email} aus dieser Reise entfernen?`)) return
                        void fuehreAus(mitglied.email, () => entferneMitglied(mitglied.email))
                      }}
                      aria-label={`${mitglied.email} entfernen`}
                    >
                      <Trash2 size={14} /> Entfernen
                    </button>
                  </div>
                ) : (
                  <span className="marker">{rolleName(mitglied.role)}</span>
                )}
              </li>
            )
          })}
        </ul>
      )}

      {fehler && <p className="hinweis">{fehler}</p>}

      {darfVerwalten && <MitgliedHinzu onFehler={setFehler} />}

      {darfVerwalten && (
        <p className="abschluss">
          Berechtigt wird über die E-Mail-Adresse. Wer die App noch nie geöffnet hat, lässt sich
          trotzdem eintragen — beim ersten Anmelden trifft seine Adresse auf die Mitgliedschaft. Die
          eigene Zeile kann nur eine andere Person mit der Rolle Verwalten ändern. Tagebuchbeiträge
          bleiben davon unberührt: ändern darf sie nur, wer sie geschrieben hat, auch die Verwaltung
          nicht.
        </p>
      )}
    </div>
  )
}

/** Einladen: Adresse eintippen, Rolle wählen, fertig. */
function MitgliedHinzu({ onFehler }: { onFehler: (text: string | null) => void }) {
  const { setzeMitglied } = useTrip()
  const [email, setEmail] = useState('')
  const [rolle, setRolle] = useState<Role>('modify')
  const [laeuft, setLaeuft] = useState(false)

  const gueltig = istEmail(email)

  async function eintragen() {
    if (!gueltig || laeuft) return
    setLaeuft(true)
    onFehler(null)
    try {
      await setzeMitglied(normalisiereEmail(email), rolle)
      setEmail('')
    } catch (err) {
      console.error('Mitglied eintragen fehlgeschlagen', err)
      onFehler(fehlerSatz(err))
    } finally {
      setLaeuft(false)
    }
  }

  return (
    <>
      <div className="raster-2">
        <Field label="E-Mail-Adresse">
          <input
            type="email"
            value={email}
            autoComplete="off"
            placeholder="name@beispiel.ch"
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void eintragen()
            }}
          />
        </Field>
        <Field label="Rolle">
          <select value={rolle} onChange={(e) => setRolle(e.target.value as Role)}>
            {ROLLEN.map((r) => (
              <option key={r} value={r}>
                {rolleName(r)}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <div className="knopfreihe">
        <button
          type="button"
          className="knopf"
          disabled={!gueltig || laeuft}
          onClick={() => void eintragen()}
        >
          {laeuft ? <Loader2 size={14} className="dreht" /> : <Plus size={14} />} Mitglied eintragen
        </button>
      </div>
      {email !== '' && !gueltig && (
        <p className="hinweis">Das sieht nicht nach einer E-Mail-Adresse aus.</p>
      )}
    </>
  )
}

/** Die Reise samt allem, was daran hängt. Zwei Schritte, kein Versehen. */
function ReiseLoeschen() {
  const { aktiv, loeschen } = useTrip()
  const [sicher, setSicher] = useState(false)
  const [laeuft, setLaeuft] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)

  return (
    <div className="block">
      <h4>Reise löschen</h4>
      <p>
        Löscht die Reise für alle Mitglieder, mit allen Etappen, Tagebucheinträgen, Ausgaben und
        Fotos auf dem Server. Das ist nicht rückgängig zu machen. Im Zweifel vorher im Reiter Daten
        einen Export anlegen.
      </p>
      <div className="knopfreihe">
        {sicher ? (
          <>
            <button
              type="button"
              className="knopf gefahr"
              disabled={laeuft}
              onClick={() => {
                setLaeuft(true)
                setFehler(null)
                void loeschen()
                  .catch((err: unknown) => {
                    console.error('Reise löschen fehlgeschlagen', err)
                    setFehler(fehlerSatz(err))
                  })
                  .finally(() => {
                    setLaeuft(false)
                  })
              }}
            >
              {laeuft ? <Loader2 size={14} className="dreht" /> : <Trash2 size={14} />} „
              {aktiv?.name}“ wirklich löschen
            </button>
            <button type="button" className="knopf still" onClick={() => setSicher(false)}>
              Abbrechen
            </button>
          </>
        ) : (
          <button type="button" className="knopf gefahr" onClick={() => setSicher(true)}>
            <Trash2 size={14} /> Reise löschen
          </button>
        )}
      </div>
      {fehler && <p className="hinweis">{fehler}</p>}
    </div>
  )
}
